import React from "react";

/**
 * Premium feature section with a large responsive video player.
 * Designed to feel like a cinematic product moment, not a card.
 *
 * Props
 *  - kind:        "mp4" | "youtube" | "vimeo" | "coming-soon"
 *  - src:         MP4 URL, or YouTube/Vimeo video ID
 *  - poster:      Thumbnail image URL
 *  - eyebrow:     Small label (e.g. "01 · The Vision")
 *  - title:       Section headline
 *  - description: Supporting paragraph
 *  - layout:      "media-left" (default) or "media-right" — alternates editorial rhythm
 *  - duration:    Optional badge ("1:24")
 *
 * The player preserves the source video's true aspect ratio (no cropping, no zooming).
 * Built so future videos swap by props alone — no layout edits required.
 */

const DEFAULT_ASPECT = "16 / 9";
const ASPECT_TRANSITION = "aspect-ratio 350ms cubic-bezier(0.25,1,0.5,1)";

function VideoPlayer({ kind, src, poster, title, onLoadedMetadata }) {
  if (kind === "mp4" && src) {
    return (
      <video
        src={src}
        poster={poster}
        className="absolute inset-0 w-full h-full object-contain bg-black"
        controls
        autoPlay
        playsInline
        onLoadedMetadata={onLoadedMetadata}
        data-testid="video-player-mp4"
      />
    );
  }
  if (kind === "youtube" && src) {
    return (
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube.com/embed/${src}?autoplay=1&rel=0&modestbranding=1`}
        title={title}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        data-testid="video-player-youtube"
      />
    );
  }
  if (kind === "vimeo" && src) {
    return (
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://player.vimeo.com/video/${src}?autoplay=1&title=0&byline=0&portrait=0`}
        title={title}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        data-testid="video-player-vimeo"
      />
    );
  }
  return null;
}

function VideoPoster({ kind, poster, duration, playable, title }) {
  return (
    <>
      {poster && (
        <img
          src={poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-80 transition-opacity duration-700"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center">
        {kind === "coming-soon" ? (
          <div className="text-center px-6">
            <div className="overline text-brand">In Production</div>
            <div className="font-display text-3xl md:text-4xl text-primary mt-3">
              Live demonstration arriving shortly
            </div>
          </div>
        ) : (
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border border-brand-hi flex items-center justify-center group-hover:scale-110 transition-transform duration-500 bg-black/30 backdrop-blur-sm">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-brand translate-x-[2px]" aria-label={playable ? `Play: ${title}` : "Demo coming soon"}>
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}
      </div>
      {duration && (
        <div className="absolute top-6 right-6 px-3 py-1 border border-brand-hi text-xs uppercase tracking-widest text-brand bg-black/40 backdrop-blur-sm">
          {duration}
        </div>
      )}
    </>
  );
}

function VideoText({ eyebrow, title, description, textOrder }) {
  return (
    <div className={`lg:col-span-5 ${textOrder}`}>
      {eyebrow && <div className="overline">{eyebrow}</div>}
      <h3 className="font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.08] mt-6">
        {title}
      </h3>
      {description && (
        <p className="text-secondary text-lg mt-8 leading-relaxed">{description}</p>
      )}
    </div>
  );
}

export default function VideoShowcase({
  kind = "coming-soon",
  src,
  poster,
  eyebrow,
  title,
  description,
  layout = "media-left",
  duration,
}) {
  const [playing, setPlaying] = React.useState(false);
  const [vidAspect, setVidAspect] = React.useState(null);

  const onLoadedMetadata = (e) => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight) {
      setVidAspect(`${v.videoWidth} / ${v.videoHeight}`);
    }
  };

  const containerStyle = {
    aspectRatio: (playing && kind === "mp4" && vidAspect) ? vidAspect : DEFAULT_ASPECT,
    transition: ASPECT_TRANSITION,
  };

  const playable = kind !== "coming-soon" && src;
  const mediaOrder = layout === "media-right" ? "lg:order-2" : "lg:order-1";
  const textOrder = layout === "media-right" ? "lg:order-1" : "lg:order-2";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid="video-feature">
      <div className={`lg:col-span-7 ${mediaOrder}`}>
        <div className="relative w-full overflow-hidden bg-black border border-brand" style={containerStyle}>
          {playing ? (
            <VideoPlayer kind={kind} src={src} poster={poster} title={title} onLoadedMetadata={onLoadedMetadata} />
          ) : (
            <button
              type="button"
              onClick={() => playable && setPlaying(true)}
              className={`absolute inset-0 w-full h-full group ${playable ? "cursor-pointer" : "cursor-default"}`}
              aria-label={playable ? `Play: ${title}` : "Demo coming soon"}
              data-testid={`video-play-${kind}`}
            >
              <VideoPoster kind={kind} poster={poster} duration={duration} playable={playable} title={title} />
            </button>
          )}
        </div>
      </div>
      <VideoText eyebrow={eyebrow} title={title} description={description} textOrder={textOrder} />
    </div>
  );
}
